import { Button } from "@/src/components/atom/Button";
import Dialog from "@/src/components/atom/Dialog";
import DialogFooterContainer from "@/src/components/atom/Dialog/DialogFooterContainer";
import CancelAlertDialog from "@/src/components/molecule/CancelAlertDialog";
import ContactFormItem from "@/src/components/molecule/ContactFormItem";
import FormItem from "@/src/components/molecule/FormItem";
import { emailRegex } from "@/src/utils/regex";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

interface BuyerEmployeeAddDialogProps {
  defaultCountryCode?: string;
  onSave: (values: {
    name: string;
    email: string;
    isActivated: string;
    personalContact?: string;
    officeContact?: string;
    buyerEmployeeRemark?: string;
    personalContactPrefix?: string;
    officeContactPrefix?: string;
  }) => void;
  onClose: () => void;
}

const DEFAULT_VALUE = {
  name: "",
  email: "",
  personalContact: "",
  officeContact: "",
  isActivated: "Y",
  buyerEmployeeRemark: "",
};

const BuyerEmployeeAddDialog = ({
  defaultCountryCode,
  onClose,
  onSave,
}: BuyerEmployeeAddDialogProps) => {
  const { t } = useTranslation();
  const { handleSubmit, control, register, setValue, watch, setFocus } =
    useForm({
      mode: "onBlur",
      reValidateMode: "onBlur",
      defaultValues: {
        ...DEFAULT_VALUE,
        personalContactPrefix: defaultCountryCode,
        officeContactPrefix: defaultCountryCode,
      },
    });
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState<boolean>(false);

  useEffect(() => {
    setFocus("name");
  }, [setFocus]);

  return (
    <Dialog
      title={t("clientManagement:importer.buyerListAddPage.addContactPerson")}
      open
      onOpenChange={onClose}
      destroyDialogWhenEscapePress={false}
      onEscapeKeyDown={() => setIsCancelAlertOpen(true)}
      footer={
        <DialogFooterContainer>
          <Button
            buttonColor="black"
            buttonGrade="tertiary"
            onClick={() => setIsCancelAlertOpen(true)}
          >
            {t("clientManagement:importer.common.exit")}
          </Button>
          <Button onClick={() => ref.current?.click()}>
            {t("clientManagement:importer.common.save")}
          </Button>
        </DialogFooterContainer>
      }
    >
      <Form onSubmit={handleSubmit(onSave)}>
        <FormItem
          type="text"
          direction="vertical"
          label={t("clientManagement:importer.common.name")}
          control={control}
          name="name"
          rules={{ required: true }}
          inputProps={{
            placeholder: t("clientManagement:importer.placeholder.name"),
          }}
          errorsMessage={{
            required: t("error:required"),
          }}
        />
        <FormItem
          type="text"
          direction="vertical"
          label={t("clientManagement:importer.common.email")}
          control={control}
          name="email"
          rules={{ required: true, pattern: emailRegex }}
          inputProps={{
            placeholder: t("clientManagement:importer.placeholder.email"),
          }}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("clientManagement:importer.common.personalContact")}
          direction="vertical"
          prefixName="personalContactPrefix"
          restContactName="personalContact"
          prefixValue={watch("personalContactPrefix") || undefined}
          isRequired={false}
        />
        <ContactFormItem
          register={register}
          setValue={setValue}
          label={t("clientManagement:importer.common.officeContact")}
          direction="vertical"
          prefixName="officeContactPrefix"
          restContactName="officeContact"
          prefixValue={watch("officeContactPrefix") || undefined}
          isRequired={false}
        />
        <FormItem
          type="radioGroup"
          direction="vertical"
          label={t("clientManagement:importer.common.use")}
          control={control}
          name="isActivated"
          rules={{ required: true }}
          options={[
            { label: "Y", value: "Y" },
            { label: "N", value: "N" },
          ]}
          errorsMessage={{
            required: t("error:required"),
            pattern: t("error:login.emailTypeMissMatching"),
          }}
        />

        <FormItem
          type="textarea"
          name="buyerEmployeeRemark"
          direction="vertical"
          label={t("clientManagement:importer.common.remark")}
          control={control}
          inputProps={{
            style: { width: "100%", height: "150px" },
            placeholder: t("clientManagement:importer.placeholder.remark"),
            maxLength: 100,
          }}
        />

        {isCancelAlertOpen && (
          <CancelAlertDialog
            open={isCancelAlertOpen}
            onOpenChange={setIsCancelAlertOpen}
            onOk={() => {
              onClose();
              setIsCancelAlertOpen(false);
            }}
          />
        )}

        <Hidden type="submit" ref={ref} />
      </Form>
    </Dialog>
  );
};

export default BuyerEmployeeAddDialog;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Hidden = styled.button`
  position: absolute;
  width: 0;
  height: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
`;
